import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeftLong } from "@fortawesome/pro-regular-svg-icons";
import HeaderBar from "../../components/HeaderBar";
import Sidebar from "../../components/Sidebar";
import { useAuth } from "../../providers/AuthContext";
import toast from "react-hot-toast";
import Breadcrumbs from "../../components/Breadcrumbs";
import EditForm from "../../components/EditForm";
import TableView from "../../components/TableView";
import { useSidebar } from "../../providers/SidebarContext";
import AgreementServices from "../../components/agreement/AgreementServices";
import { getUsersByAgreement } from "../../api/Users";

const FinancialPage = () => {
  const { syncData } = useSidebar();
  const { getAccessToken, user } = useAuth();
  const { agreementPublicId, tenantPublicId } = useParams();
  const navigate = useNavigate();
  const [agreement, setAgreement] = useState(null);
  const [contactPerson, setContactPerson] = useState(null);
  const [loadingFinancials, setLoadingFinancials] = useState(true);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const [activeTab, setActiveTab] = useState(
    query.get("tab") ? query.get("tab") : "details"
  );
  const [services, setServices] = useState([]);


  useEffect(() => {
    if (agreementPublicId) {
      handleGetUsersByAgreement();
    }
  }, [agreementPublicId]);

  useEffect(() => {
    const tab = query.get("tab");
    if (tab) {
      setActiveTab(tab);
    }
  }, [location.search]);

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    query.set("tab", activeTab);
    navigate(`${location.pathname}?${query.toString()}`, { replace: true });
  }, [activeTab, location.pathname, navigate]);

  const handleGetUsersByAgreement = async () => {
    try {
      const accessToken = await getAccessToken();
      const users = await getUsersByAgreement(agreementPublicId, accessToken);
      setFilteredUsers(users);
      setLoadingUsers(false);
      setLoadingFinancials(false);
    } catch (error) {
      console.error("Failed to fetch users:", error);
      setLoadingUsers(false);
    }
  };

  const usersWithoutAccountType = filteredUsers.filter(
    (user) => user.accountTypeName === "Default"
  );

  const usersInactiveAccountType = filteredUsers.filter(
    (user) => user.accountTypeStatus === "Inactive"
  );

  const usersWithoutBillingGroup = filteredUsers.filter(
    (user) => !user.billingGroupName
  );

  const usersInactiveBillingGroup = filteredUsers.filter(
    (user) => user.billingGroupStatus === "Inactive"
  );

  const usersO365 = filteredUsers.filter(
    (user) => !user.O365
  );

  const handleBackClick = () => {
    navigate(-1);
  };

  console.log("CONTACT PERSON:", contactPerson);
  console.log("AGREEMENT:", agreement);
  console.log("🔍 Filtered users:", filteredUsers);

  return (
    <>
      <HeaderBar />
      <div className="flex flex-row w-full">
        <Sidebar />
        <main className="page">
          <div className={`${loadingFinancials ? "loading" : ""} page-content`}>
            <Breadcrumbs />
            <div className="flex flex-row items-center pb-4">
              <h2 className="">Financials</h2>
              <div className="flex gap-4 ml-auto">
                <button
                  className="btn btn-outline-primary hidden"
                  onClick={handleBackClick}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary hidden"
                  disabled={!hasChanges}
                  onClick={handleBackClick}
                >
                  Save
                </button>
              </div>
            </div>
            <div className="border-b border-mute-200 mb-4 gap-4 flex flex-col">
              <div className="flex justify-between items-center">
                <h2>Users</h2>
              </div>
              <div className="flex flex-row">
                <button
                  className={`px-4 pb-2 ${
                    activeTab === "without-account"
                      ? "border-b-2 border-black font-bold text-sm text-black"
                      : "text-mute-400 text-sm font-bold"
                  }`}
                  onClick={() => setActiveTab("without-account")}
                >
                  No account-type ({usersWithoutAccountType.length})
                </button>
                <button
                  className={`px-4 pb-2 ${
                    activeTab === "inactive-account"
                      ? "border-b-2 border-black font-bold text-sm text-black"
                      : "text-mute-400 text-sm font-bold"
                  }`}
                  onClick={() => setActiveTab("inactive-account")}
                >
                  Inactive account-type ({usersInactiveAccountType.length})
                </button>
                <button
                  className={`px-4 pb-2 ${
                    activeTab === "without-billing-group"
                      ? "border-b-2 border-black font-bold text-sm text-black"
                      : "text-mute-400 text-sm font-bold"
                  }`}
                  onClick={() => setActiveTab("without-billing-group")}
                >
                  No billing group ({usersWithoutBillingGroup.length})
                </button>
                <button
                  className={`px-4 pb-2 ${
                    activeTab === "inactive-billing-group"
                      ? "border-b-2 border-black font-bold text-sm text-black"
                      : "text-mute-400 text-sm font-bold"
                  }`}
                  onClick={() => setActiveTab("inactive-billing-group")}
                >
                  Inactive billing group ({usersInactiveBillingGroup.length})
                </button>
                <button
                  className={`px-4 pb-2 ${
                    activeTab === "O365-license"
                      ? "border-b-2 border-black font-bold text-sm text-black"
                      : "text-mute-400 text-sm font-bold"
                  }`}
                  onClick={() => setActiveTab("O365-license")}
                >
                  O365 license without service ({usersO365.length})
                </button>
              </div>
            </div>

            <div className="flex flex-col px-4 py-8 shadow-lg border rounded-md w-full gap-4">
              {agreementPublicId && activeTab && (
                <>
                  <TableView
                    data={
                      activeTab === "without-account"
                        ? usersWithoutAccountType
                        : activeTab === "inactive-account"
                        ? usersInactiveAccountType
                        : activeTab === "without-billing-group"
                        ? usersWithoutBillingGroup
                        : activeTab === "inactive-billing-group"
                        ? usersInactiveBillingGroup
                        : activeTab === "O365-license"
                        ? usersO365                        
                        : []
                    }
                    selectedRows={selectedUsers} // Map selectedUsers to selectedRows
                    setSelectedRows={setSelectedUsers} // Ensure selection updates correctly
                    noCheckbox={true}
                    columns={[
                      { key: "userPublicId", label: "User ID" },
                      { key: "displayName", label: "Name" },
                      { key: "userPrincipalName", label: "Principal name" },
                      { key: "accountTypeName", label: "Account name" },
                      { key: "accountTypeStatus", label: "Account status" },
                      { key: "billingGroupId", label: "Billing group ID" },
                      { key: "billingGroupName", label: "Billing group" },
                      { key: "billingGroupStatus", label: "Billing status" },
                      { key: "mobilePhone", label: "Phone" },
                      { key: "managerDisplayName", label: "Manager" },
                      { key: "lastSignInDateTime", label: "Last login" },
                    ]}
                    primaryKey="userPublicId" // Match this with userPublicId
                    basePath={`/${agreementPublicId}/tenant/${tenantPublicId}/user`}
                  />
                </>
              )}
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default FinancialPage;
